'use client';

import { persistLandingClickIds } from 'lib/analytics/landingParams';
import { NextStackBanner } from 'components/shared/consent/NextStackBanner';
import { LEGACY_CONSENT_EVENT } from 'components/shared/consent/useLegacyCookieAccepted';
import React, {
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const LOCAL_STORAGE_COOKIE_CONSENT_KEY = 'COOKIE_CONSENT';

const CookieConsentContext = createContext<
  readonly [boolean | null | undefined, (v: boolean | null) => unknown]
>([false, () => {}]);

export const CookieConsentBannerContext = createContext<boolean | null>(false);
export const CookieConsentBannerProvider = CookieConsentBannerContext.Provider;

export const CookieConsentProvider = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const [cookieConsent, setCookieConsent] = useState<
    boolean | null | undefined
  >(undefined);

  useEffect(() => {
    if (cookieConsent === undefined) {
      const storedConsent = getCookieConsentFromLocalStorage();
      setCookieConsent(storedConsent);
      return;
    }

    if (cookieConsent !== undefined) {
      setCookieConsentToLocalStorage(cookieConsent);
    }
  }, [cookieConsent]);

  const value = useMemo(() => {
    return [cookieConsent, setCookieConsent] as const;
  }, [cookieConsent, setCookieConsent]);

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  return useContext(CookieConsentContext);
};

const CookieConsentBannerSwitch = ({ className }: { className?: string }) => {
  return <NextStackBanner className={className} />;
};

export const CookieConsentBanner = memo(CookieConsentBannerSwitch);
CookieConsentBanner.displayName = 'CookieConsentBanner';

const getCookieConsentFromLocalStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  const item = window.localStorage.getItem(LOCAL_STORAGE_COOKIE_CONSENT_KEY);
  if (item === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(item);
    return parsed;
  } catch (error) {
    console.warn('Failed to parse cookie consent from localStorage:', error);
    return null;
  }
};

const setCookieConsentToLocalStorage = (value: boolean | null) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(
    LOCAL_STORAGE_COOKIE_CONSENT_KEY,
    JSON.stringify(value),
  );
  if (value === true) persistLandingClickIds();
  window.dispatchEvent(new Event(LEGACY_CONSENT_EVENT));
};

export default CookieConsentBanner;

export const useScript = (
  attributes: ({ src: string } | { body: string }) & {
    options?: { [key: string]: string };
    head?: boolean;
    onload?: () => void;
  },
) => {
  useEffect(() => {
    const script = document.createElement('script');
    if (attributes.onload)
      script.onload = attributes.onload as (
        this: GlobalEventHandlers,
        ev: Event,
      ) => any;
    script.type = 'text/javascript';
    if ('src' in attributes) {
      script.src = attributes.src;
    } else {
      script.innerHTML = attributes.body;
    }
    if (attributes.options) {
      Object.entries(attributes.options).forEach(([key, value]) => {
        script.setAttribute(key, value as string);
      });
    }

    if (attributes.head) document.body.appendChild(script);
    else document.body.appendChild(script);

    return () => {
      if (attributes.head) document.body.removeChild(script);
      else document.body.removeChild(script);
    };
  }, []);
};
